import { Navigate, Outlet, useLocation, useMatches } from 'react-router-dom';

import { ProfileButton } from '@/components/layouts/ProfileButton';
import { ArcusLoading } from '@/components/shared/ArcusLoading';
import { GuestGate, type GuestGateCopy } from '@/components/shared/GuestGate';
import { PageHeader } from '@/components/ui/page-header';
import { useSession } from '@/compositions/hooks/useSession';
import { withNextParam } from '@/utils/helpers/next-path';

/**
 * A copy do portão de uma rota, declarada no `handle` dela
 * (`src/routes/index.tsx`). `pageTitle` é o título que a tela teria — o
 * convidado vê o nome do lugar aonde tentou ir, não um "Entrar" genérico.
 */
export interface GuestGateRouteCopy extends GuestGateCopy {
  pageTitle: string;
}

export interface GuestGateRouteHandle {
  guestGate: GuestGateRouteCopy;
}

/** Só para rota sem `handle`: toda tela-destino sob a guarda deveria declarar o seu. */
const FALLBACK_COPY: GuestGateRouteCopy = {
  pageTitle: 'Arcus',
  title: 'Esta tela pede uma conta',
  description: 'Entre na sua conta ou crie uma para continuar.',
};

/**
 * `handle` é `any` no React Router; aqui ele vira `unknown` e só passa o que
 * tem o formato certo — uma rota com outro `handle` não derruba a guarda.
 */
function isGuestGateRouteHandle(handle: unknown): handle is GuestGateRouteHandle {
  if (typeof handle !== 'object' || handle === null || !('guestGate' in handle)) return false;

  const { guestGate } = handle;

  return (
    typeof guestGate === 'object' &&
    guestGate !== null &&
    'pageTitle' in guestGate &&
    'title' in guestGate &&
    'description' in guestGate
  );
}

/** A copy da rota mais funda que declarou uma. */
function useGuestGateCopy(): GuestGateRouteCopy {
  const matches = useMatches();

  // `reduce` da esquerda para a direita: a última rota que declarou vence, que
  // é a mais funda da árvore.
  return matches.reduce<GuestGateRouteCopy>(
    (copy, { handle }) => (isGuestGateRouteHandle(handle) ? handle.guestGate : copy),
    FALLBACK_COPY,
  );
}

function GuestGateScreen() {
  const copy = useGuestGateCopy();

  return (
    // A mesma casca das telas-destino: a barra continua embaixo (o guarda
    // monta dentro do `AppLayout`) e o perfil continua no topo.
    <main className="flex min-h-dvh flex-col gap-6 p-4 pb-[var(--bottom-nav-inset)]">
      <PageHeader title={copy.pageTitle} actions={<ProfileButton />} />

      <GuestGate title={copy.title} description={copy.description} extraAction={copy.extraAction} />
    </main>
  );
}

interface RequireSessionProps {
  /**
   * O que o convidado recebe:
   *
   * - `redirect` (default) — vai para o login com `?next=`. É o certo para
   *   **fluxo de tarefa** (fora do `AppLayout`): quem abriu "criar instituição"
   *   por link quer o formulário logo depois de entrar.
   * - `gate` — fica na tela, com o `GuestGate` e a copy do `handle` da rota. É
   *   o certo para **tela-destino** (com barra): quem tocou "Pedidos" na barra
   *   precisa saber o que a tela faria antes de decidir criar uma conta.
   *   Exige data router (`createBrowserRouter`), por causa do `useMatches`.
   */
  guest?: 'redirect' | 'gate';
}

/**
 * Guarda de sessão para rotas que exigem conta (plano de autenticação v2, §3).
 *
 * Diferente da `RequireAppEntry`, aqui convidado **não** passa: o destino é uma
 * ação de produção (acompanhar pedidos, notas) ou dado só para logados
 * (DV-08). Quais rotas exigem sessão não mudou na passada de UX (2026-10); o
 * que mudou é o que o convidado vê ao chegar em uma tela-destino — antes era
 * um redirecionamento seco para o login (auditoria de UX, §1.3).
 *
 * O deep link protegido não se perde nos dois modos: o `next` viaja até o
 * login e o cadastro.
 *
 * O estado de carregamento é obrigatório: sem ele, o primeiro quadro renderiza
 * com sessão nula e trataria como convidado todo usuário já autenticado, antes
 * de a sessão ser lida.
 */
export function RequireSession({ guest = 'redirect' }: RequireSessionProps) {
  const { isAuthenticated, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) return <ArcusLoading className="h-dvh" />;

  if (!isAuthenticated) {
    if (guest === 'gate') return <GuestGateScreen />;

    return <Navigate to={withNextParam('/login', location.pathname + location.search)} replace />;
  }

  return <Outlet />;
}
